import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { useEditorStore } from "../../stores/editorStore.js";
import { usePreviewStore } from "../../stores/previewStore.js";
import { Spinner } from "../ui/Spinner.js";
import { BlockHighlight } from "./BlockHighlight.js";
import { PreviewToolbar } from "./PreviewToolbar.js";

const DEFAULT_PREVIEW_WIDTH = 390;
const MIN_PREVIEW_WIDTH = 300;
const MAX_PREVIEW_WIDTH = 900;
const EDITOR_MIN_WIDTH = 420;
const PREVIEW_WIDTH_STORAGE_KEY = "pdfPreviewWidth";

function clampPreviewWidth(nextWidth: number): number {
  const viewportMaxWidth = window.innerWidth - EDITOR_MIN_WIDTH;
  const maxWidth = Math.max(MIN_PREVIEW_WIDTH, Math.min(MAX_PREVIEW_WIDTH, viewportMaxWidth));
  return Math.min(Math.max(nextWidth, MIN_PREVIEW_WIDTH), maxWidth);
}

function readStoredPreviewWidth(): number {
  const stored = Number(localStorage.getItem(PREVIEW_WIDTH_STORAGE_KEY));
  return Number.isFinite(stored) && stored > 0 ? clampPreviewWidth(stored) : DEFAULT_PREVIEW_WIDTH;
}

function PdfCanvasPreview({ url }: { url: string }) {
  const [pages, setPages] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPages([]);
    setError(null);

    import("pdfjs-dist").then(async (lib) => {
      const workerUrl = new URL("pdfjs-dist/build/pdf.worker.mjs", import.meta.url).href;
      lib.GlobalWorkerOptions.workerSrc = workerUrl;

      const pdf = await lib.getDocument({ url, disableStream: false }).promise;
      const nextPages: string[] = [];
      for (let pageNo = 1; pageNo <= pdf.numPages; pageNo += 1) {
        if (cancelled) return;
        const page = await pdf.getPage(pageNo);
        const viewport = page.getViewport({ scale: 1.35 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const context = canvas.getContext("2d");
        if (!context) continue;
        await page.render({ canvas, canvasContext: context, viewport }).promise;
        nextPages.push(canvas.toDataURL("image/png"));
      }
      if (!cancelled) setPages(nextPages);
    }).catch((err: unknown) => {
      if (!cancelled) setError(err instanceof Error ? err.message : String(err));
    });

    return () => { cancelled = true; };
  }, [url]);

  if (error) {
    return (
      <div style={{ margin: 16, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: 8, padding: 12, fontSize: 13 }}>
        {error}
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 13 }}>
        プレビューを描画中…
      </div>
    );
  }

  return (
    <div style={{ height: "100%", overflowY: "auto", padding: "14px 0", background: "#64748b" }}>
      {pages.map((src, index) => (
        <div key={index} style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
          <img src={src} alt={`${index + 1}ページ`} style={{ maxWidth: "94%", boxShadow: "0 2px 12px rgba(15,23,42,.35)", background: "#fff" }} />
        </div>
      ))}
    </div>
  );
}

export function PreviewPane({ onHide }: { onHide?: () => void }) {
  const [width, setWidth] = useState(readStoredPreviewWidth);
  const [isResizing, setIsResizing] = useState(false);
  const dragState = useRef<{ startX: number; startWidth: number } | null>(null);
  const blobUrl = usePreviewStore((state) => state.blobUrl);
  const isGenerating = usePreviewStore((state) => state.isGenerating);
  const error = usePreviewStore((state) => state.error);
  const blockMap = usePreviewStore((state) => state.blockMap);
  const hoveredBlockId = usePreviewStore((state) => state.hoveredBlockId);
  const selectedBlockId = useEditorStore((state) => state.selectedBlockId);
  const highlighted = blockMap.find((entry) => entry.id === (hoveredBlockId ?? selectedBlockId));

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      if (!dragState.current) return;
      const nextWidth = dragState.current.startWidth + dragState.current.startX - event.clientX;
      setWidth(clampPreviewWidth(nextWidth));
    };
    const handlePointerUp = () => {
      dragState.current = null;
      setIsResizing(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(PREVIEW_WIDTH_STORAGE_KEY, String(width));
  }, [width]);

  useEffect(() => {
    function handleWindowResize() {
      setWidth((currentWidth) => clampPreviewWidth(currentWidth));
    }

    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  useEffect(() => {
    setWidth((currentWidth) => clampPreviewWidth(currentWidth));
  }, []);

  const startResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    dragState.current = { startX: event.clientX, startWidth: width };
    setIsResizing(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  return (
    <section style={{ width, minWidth: MIN_PREVIEW_WIDTH, maxWidth: MAX_PREVIEW_WIDTH, background: "#eef2f7", borderLeft: "1px solid #e2e8f0", display: "flex", flexDirection: "column", position: "relative", flexShrink: 0 }}>
      <button
        type="button"
        aria-label="PDFプレビューの幅を変更"
        title="ドラッグしてPDFプレビューの幅を調整"
        onPointerDown={startResize}
        onDoubleClick={() => setWidth(DEFAULT_PREVIEW_WIDTH)}
        style={{
          position: "absolute",
          left: -6,
          top: 0,
          bottom: 0,
          width: 12,
          border: "none",
          background: "transparent",
          cursor: "col-resize",
          padding: 0,
          zIndex: 4,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 4,
            top: "50%",
            width: 2,
            height: 64,
            transform: "translateY(-50%)",
            borderRadius: 999,
            background: isResizing ? "#14b8a6" : "#94a3b8",
            boxShadow: isResizing ? "0 0 0 3px rgba(20,184,166,0.18)" : "none",
          }}
        />
      </button>
      <PreviewToolbar onHide={onHide} />
      <div style={{ position: "relative", flex: 1, overflow: "hidden" }}>
        {isGenerating && (
          <div style={{ position: "absolute", top: 10, right: 10, zIndex: 2, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 999, padding: "5px 9px", display: "flex", alignItems: "center", gap: 6 }}>
            <Spinner size={14} />
            <span style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>生成中</span>
          </div>
        )}
        {error && (
          <div style={{ margin: 16, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", borderRadius: 8, padding: 12, fontSize: 13 }}>
            {error}
          </div>
        )}
        {blobUrl ? (
          <>
            <PdfCanvasPreview url={blobUrl} />
            {highlighted && <BlockHighlight bounds={highlighted.bounds} />}
          </>
        ) : !error ? (
          <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 13, textAlign: "center", padding: 24 }}>
            テンプレートを編集するとプレビューが表示されます。
          </div>
        ) : null}
      </div>
    </section>
  );
}
